import { Fragment } from 'react';
import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';

import type { SanityValuesType } from '@/types/sanity';

export interface ValuesProps extends SanityValuesType {}

const Values = ({ heading, image, values }: ValuesProps) => {
  return (
    <Section backgroundColour="--background-subtle">
      <HeadingLevel>
        <HeadingContainer>
          <RichHeading heading={heading} size="headline-lg" />
        </HeadingContainer>
        <Image
          {...image}
          sizes="(max-width: 1440px) 100vw, 1280px"
          radius="l"
        />
        <ValuesContainer>
          {values.map((v) => (
            <ValueContainer key={v._key}>
              <HeadingLevel>
                <Heading size="headline-sm">
                  <TextToHtml text={v.title} />
                </Heading>
              </HeadingLevel>
              <PortableText value={v.description} />
            </ValueContainer>
          ))}
        </ValuesContainer>
      </HeadingLevel>
    </Section>
  );
};

const TextToHtml = ({ text }) => {
  text.split('\n');
  return (
    <>
      {text.split('\n').map((t) => (
        <Fragment key={t}>
          {t}
          <br />
        </Fragment>
      ))}
    </>
  );
};

const HeadingContainer = styled.div`
  text-align: center;
  padding-top: var(--spacing-1600);
  padding-bottom: var(--spacing-800);

  ${screen.lg} {
    max-width: 875px;
    margin: auto;
  }
`;

const ValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--spacing-500);
  margin-bottom: var(--spacing-1600);
  ${screen.lg} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--spacing-800);
  text-align: center;

  ${screen.lg} {
    margin-top: 0;
  }

  ${Heading} {
    max-width: 265px;

    ${screen.md} {
      max-width: 350px;
    }
  }

  ${Paragraph} {
    max-width: 265px;

    &:first-of-type {
      margin-top: var(--spacing-500);
    }

    & + ${Paragraph} {
      margin-top: 6pt;
    }

    ${screen.md} {
      max-width: 370px;
    }
  }
`;

export { Values };
